import React, { useState, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  Send,
  MessageSquare,
  User,
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock,
  // Inquiry-type icons (replacing the old emoji set):
  LifeBuoy,
  Lightbulb,
  Bug,
  CreditCard,
  Handshake,
  HelpCircle,
  MessageCircle,
  MoreHorizontal,
  // Layout / state icons:
  Lock,
  Database,
  Settings as SettingsIcon,
  ChevronRight,
  ArrowRight,
  Hash,
} from 'lucide-react';
import { theme } from '../styles/theme';
import { showToast } from '../utils/toast';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  0% { opacity: 0; transform: scale(0.85); }
  60% { opacity: 1; transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const pulseRed = keyframes`
  0%, 100% { color: ${theme.colors.error[600]}; }
  50%      { color: ${theme.colors.error[700]}; }
`;

const urgentPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${theme.colors.error[500]}30; }
  50%      { box-shadow: 0 0 0 6px ${theme.colors.error[500]}00; }
`;

// =============================================================================
// PAGE-LEVEL HEADER (left-aligned, matches ProfilePage / SettingsPage)
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  animation: ${fadeUp} 0.45s ease-out both;

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const HeroTitle = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 540px;
`;

// Right-side response-time pill — uses success-tone colours so it reads
// "we're online and ready" rather than "preview / coming soon".
const ResponsePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 4px ${theme.colors.success[500]}20;
    flex-shrink: 0;
    animation: ${urgentPulse} 2.5s ease-in-out infinite;
  }

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

// =============================================================================
// TWO-COLUMN BODY LAYOUT
// =============================================================================

const Body = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: ${theme.spacing[6]};
  align-items: start;
  animation: ${fadeUp} 0.55s ease-out both;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

// Sticky on desktop so the reply promise stays visible while the form scrolls.
const InfoRail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  position: sticky;
  top: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    position: static;
    top: auto;
  }
`;

// =============================================================================
// REPLY-WINDOW NAVY CARD (the page's emotional anchor)
// =============================================================================

const ReplyCard = styled.div`
  position: relative;
  padding: ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};
  box-shadow: ${theme.shadows.md};

  /* Grain overlay for tactile depth on the deep navy. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }

  /* Soft glow accent in the corner. */
  &::after {
    content: '';
    position: absolute;
    top: -90px;
    right: -90px;
    width: 240px;
    height: 240px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }
`;

const ReplyEyebrow = styled.div`
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 3px ${theme.colors.success[500]}33;
  }
`;

const ReplyTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 26px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[5]};
  letter-spacing: -0.025em;
  line-height: 1.15;
`;

const PromiseList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const PromiseItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.5;

  & .icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: ${theme.borderRadius.sm};
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & .strong {
    display: block;
    color: #fff;
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.sm};
    letter-spacing: -0.005em;
  }

  & .lede {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    font-family: ${theme.typography.fontFamily.mono};
  }
`;

// =============================================================================
// "LOOKING FOR" QUICK-LINKS PANEL
// =============================================================================

const LinksCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]};
  box-shadow: ${theme.shadows.sm};
`;

const LinksHeading = styled.div`
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[3]};
`;

const LinkRow = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} 0;
  border-bottom: 1px dashed ${theme.colors.borderLight};
  text-decoration: none;
  color: inherit;
  transition: padding ${theme.transitions.base};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: ${theme.spacing[2]};
  }

  & .icon {
    width: 32px;
    height: 32px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[500]};
    border: 1px solid ${theme.colors.primary[100]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background ${theme.transitions.base}, color ${theme.transitions.base};
  }

  &:hover .icon {
    background: ${theme.colors.primary[500]};
    color: #fff;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & .label {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    letter-spacing: -0.005em;
  }

  & .desc {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
    margin-top: 2px;
    line-height: 1.4;
  }

  & .chev {
    color: ${theme.colors.textMuted};
    transition: transform ${theme.transitions.base}, color ${theme.transitions.base};
    flex-shrink: 0;
  }

  &:hover .chev {
    color: ${theme.colors.primary[500]};
    transform: translateX(2px);
  }
`;

// =============================================================================
// FORM CARD SHELL
// =============================================================================

const FormCard = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  position: relative;
`;

const FormBody = styled.div`
  padding: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[6]} ${theme.spacing[5]};
  }
`;

// Numbered sub-section scaffolding (matches ProfilePage / SettingsPage).
const SubSection = styled.div<{ $first?: boolean }>`
  ${({ $first }) =>
    $first
      ? css`
          padding-top: 0;
        `
      : css`
          margin-top: ${theme.spacing[10]};
          padding-top: ${theme.spacing[8]};
          border-top: 1px solid ${theme.colors.borderLight};
        `}
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[5]};
  max-width: 580px;
`;

// =============================================================================
// FORM FIELDS
// =============================================================================

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[5]} ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }
`;

const FormField = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  ${({ $fullWidth }) => $fullWidth && css`grid-column: 1 / -1;`}
`;

const FieldLabel = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[500]};
  }

  & .req {
    color: ${theme.colors.error[500]};
    font-weight: ${theme.typography.fontWeight.bold};
    margin-left: 2px;
  }
`;

const inputBase = css<{ $hasError?: boolean }>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${({ $hasError }) =>
    $hasError ? theme.colors.error[500] : theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};
  width: 100%;

  &:hover:not(:disabled) {
    border-color: ${({ $hasError }) =>
      $hasError ? theme.colors.error[500] : theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${({ $hasError }) =>
      $hasError ? theme.colors.error[500] : theme.colors.primary[500]};
    background: ${theme.colors.surface};
    box-shadow:
      0 0 0 3px
        ${({ $hasError }) =>
          $hasError ? `${theme.colors.error[500]}1a` : `${theme.colors.primary[500]}1a`},
      0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:disabled {
    background: ${theme.colors.backgroundTertiary};
    color: ${theme.colors.textSecondary};
    cursor: not-allowed;
  }
`;

const TextInput = styled.input<{ $hasError?: boolean }>`
  ${inputBase}
`;

const TextAreaField = styled.textarea<{ $hasError?: boolean }>`
  ${inputBase}
  min-height: 160px;
  resize: vertical;
  line-height: ${theme.typography.lineHeight.relaxed};
  font-family: inherit;
`;

const FieldError = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 4px ${theme.spacing[3]};
  background: ${theme.colors.error[100]};
  color: ${theme.colors.error[700]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  width: max-content;
  max-width: 100%;
`;

// =============================================================================
// CHARACTER COUNTER (mirrors ProfilePage's CharCounter exactly)
// =============================================================================

const CounterWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[1]};
`;

const CounterTrack = styled.div`
  flex: 1;
  height: 3px;
  background: ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const CounterFill = styled.div<{ $pct: number; $tone: 'normal' | 'warn' | 'danger' }>`
  height: 100%;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $tone }) =>
    $tone === 'danger'
      ? theme.colors.error[500]
      : $tone === 'warn'
      ? theme.colors.warning[500]
      : theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  transition: width 0.25s ease, background-color 0.25s ease;
`;

const CounterLabel = styled.span<{ $tone: 'normal' | 'warn' | 'danger' }>`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-family: ${theme.typography.fontFamily.mono};
  white-space: nowrap;

  ${({ $tone }) =>
    $tone === 'danger'
      ? css`
          color: ${theme.colors.error[600]};
          font-weight: ${theme.typography.fontWeight.semibold};
          animation: ${pulseRed} 1.4s ease-in-out infinite;
        `
      : $tone === 'warn'
      ? css`
          color: ${theme.colors.warning[700]};
          font-weight: ${theme.typography.fontWeight.medium};
        `
      : css`
          color: ${theme.colors.textMuted};
        `}
`;

interface CharCounterProps {
  value: string;
  max: number;
}

const CharCounter: React.FC<CharCounterProps> = ({ value, max }) => {
  const len = value.length;
  const pct = max > 0 ? (len / max) * 100 : 0;
  const tone: 'normal' | 'warn' | 'danger' = pct >= 95 ? 'danger' : pct >= 80 ? 'warn' : 'normal';
  return (
    <CounterWrap>
      <CounterTrack>
        <CounterFill $pct={pct} $tone={tone} />
      </CounterTrack>
      <CounterLabel $tone={tone}>
        {len.toLocaleString()} / {max.toLocaleString()}
      </CounterLabel>
    </CounterWrap>
  );
};

// =============================================================================
// INQUIRY TYPE TILE GRID
// =============================================================================

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  text-align: left;
  background: ${({ $selected }) =>
    $selected ? theme.colors.primary[50] : theme.colors.surface};
  border: 1px solid
    ${({ $selected }) => ($selected ? theme.colors.primary[200] : theme.colors.borderLight)};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: transform ${theme.transitions.base}, border-color ${theme.transitions.base},
    background ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  font-family: inherit;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $selected }) =>
      $selected ? theme.colors.primary[300] : theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
  }

  /* Left-edge accent appears on selection. */
  ${({ $selected }) =>
    $selected &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: ${theme.spacing[3]};
        bottom: ${theme.spacing[3]};
        left: 0;
        width: 3px;
        background: ${theme.colors.primary[500]};
        border-radius: 0 ${theme.borderRadius.full} ${theme.borderRadius.full} 0;
      }
    `}
`;

const TileIcon = styled.span<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: ${({ $selected }) =>
    $selected ? theme.colors.primary[500] : theme.colors.primary[50]};
  color: ${({ $selected }) => ($selected ? '#fff' : theme.colors.primary[500])};
  border: 1px solid
    ${({ $selected }) => ($selected ? theme.colors.primary[600] : theme.colors.primary[100])};
  transition: background ${theme.transitions.base}, color ${theme.transitions.base},
    border-color ${theme.transitions.base};
  box-shadow: ${({ $selected }) =>
    $selected ? '0 4px 10px -4px rgba(0, 0, 41, 0.45)' : 'none'};
`;

const TileTitle = styled.h4<{ $selected: boolean }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${({ $selected }) =>
    $selected ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  margin: ${theme.spacing[1]} 0 0;
  letter-spacing: -0.005em;
`;

const TileDesc = styled.p`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.45;
`;

const TileCheck = styled.span`
  position: absolute;
  top: ${theme.spacing[3]};
  right: ${theme.spacing[3]};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${theme.colors.primary[500]};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px -2px rgba(0, 0, 41, 0.45);
`;

// =============================================================================
// PRIORITY CHIP TOGGLE
// =============================================================================

type PriorityTone = 'muted' | 'primary' | 'warn' | 'danger';

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const PriorityChip = styled.button<{ $selected: boolean; $tone: PriorityTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  white-space: nowrap;

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  ${({ $selected, $tone }) => {
    // Resolve tone palette
    const tones = {
      muted: {
        dot: theme.colors.textMuted,
        bgUnselected: 'transparent',
        bgSelected: theme.colors.backgroundTertiary,
        textUnselected: theme.colors.textSecondary,
        textSelected: theme.colors.textPrimary,
        borderUnselected: theme.colors.border,
        borderSelected: theme.colors.borderHover,
      },
      primary: {
        dot: theme.colors.primary[500],
        bgUnselected: theme.colors.surface,
        bgSelected: theme.colors.primary[500],
        textUnselected: theme.colors.primary[700],
        textSelected: '#fff',
        borderUnselected: theme.colors.primary[200],
        borderSelected: theme.colors.primary[600],
      },
      warn: {
        dot: theme.colors.warning[500],
        bgUnselected: theme.colors.surface,
        bgSelected: theme.colors.warning[500],
        textUnselected: theme.colors.warning[700],
        textSelected: '#fff',
        borderUnselected: `${theme.colors.warning[500]}66`,
        borderSelected: theme.colors.warning[600],
      },
      danger: {
        dot: theme.colors.error[500],
        bgUnselected: theme.colors.surface,
        bgSelected: theme.colors.error[500],
        textUnselected: theme.colors.error[700],
        textSelected: '#fff',
        borderUnselected: `${theme.colors.error[500]}66`,
        borderSelected: theme.colors.error[600],
      },
    } as const;

    const t = tones[$tone];

    return css`
      background: ${$selected ? t.bgSelected : t.bgUnselected};
      color: ${$selected ? t.textSelected : t.textUnselected};
      border: 1px solid ${$selected ? t.borderSelected : t.borderUnselected};

      & .dot {
        background: ${$selected ? '#fff' : t.dot};
      }

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px -8px rgba(0, 0, 41, 0.25);
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33;
      }

      ${$tone === 'danger' && $selected
        ? css`
            animation: ${urgentPulse} 2.2s ease-in-out infinite;
          `
        : ''}
    `;
  }}
`;

// =============================================================================
// ERROR BAND (general top-of-form errors)
// =============================================================================

const ErrorBand = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.surface};
    color: ${theme.colors.error[600]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${theme.colors.error[500]}40;
  }

  & .copy {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.error[700]};
    line-height: 1.5;
    flex: 1;
    min-width: 0;
  }
`;

// =============================================================================
// STICKY ACTION BAR
// =============================================================================

const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  border-top: 1px solid ${theme.colors.borderLight};
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-radius: 0 0 ${theme.borderRadius.xl} ${theme.borderRadius.xl};
  box-shadow: 0 -8px 24px -16px rgba(0, 0, 41, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[3]} ${theme.spacing[5]};
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const StickyMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};

  & .icon {
    color: ${theme.colors.success[500]};
  }
`;

const SendButton = styled.button<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.85 : 1)};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 10px -6px rgba(0, 0, 41, 0.45);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const ButtonSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// =============================================================================
// SUCCESS STATE
// =============================================================================

const SuccessShell = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing[12]} ${theme.spacing[8]};
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
  animation: ${fadeUp} 0.5s ease-out both;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[8]} ${theme.spacing[5]};
  }
`;

const SuccessIcon = styled.div`
  width: 88px;
  height: 88px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success[100]};
  border: 4px solid ${theme.colors.surface};
  box-shadow: 0 0 0 6px ${theme.colors.success[500]}15, 0 12px 24px -10px rgba(0, 0, 41, 0.15);
  color: ${theme.colors.success[600]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[6]};
  animation: ${scaleIn} 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const SuccessTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.025em;
  line-height: 1.15;
`;

const SuccessSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[6]};
  max-width: 460px;
  margin-left: auto;
  margin-right: auto;
`;

const RefBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[10]};

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 12px;
    color: ${theme.colors.textPrimary};
    letter-spacing: 0.04em;
  }

  & .icon {
    color: ${theme.colors.primary[500]};
  }
`;

// "What happens next" timeline
const Timeline = styled.ol`
  list-style: none;
  margin: 0 auto ${theme.spacing[10]};
  padding: 0;
  max-width: 460px;
  text-align: left;
  position: relative;
`;

const TimelineStep = styled.li<{ $tone: 'done' | 'current' | 'pending' }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  padding-bottom: ${theme.spacing[5]};

  &:last-child {
    padding-bottom: 0;
  }

  /* Vertical connector line. */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 17px;
    top: 36px;
    bottom: 0;
    width: 2px;
    background: ${({ $tone }) =>
      $tone === 'done' ? theme.colors.success[500] : theme.colors.borderLight};
  }

  & .marker {
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.full};
    background: ${({ $tone }) =>
      $tone === 'done'
        ? theme.colors.success[500]
        : $tone === 'current'
        ? theme.colors.primary[500]
        : theme.colors.backgroundTertiary};
    color: ${({ $tone }) =>
      $tone === 'pending' ? theme.colors.textMuted : '#fff'};
    border: 3px solid ${theme.colors.surface};
    box-shadow: 0 0 0 1px
      ${({ $tone }) =>
        $tone === 'pending' ? theme.colors.borderLight : 'transparent'};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .body {
    flex: 1;
    min-width: 0;
    padding-top: 6px;
  }

  & .step-title {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${({ $tone }) =>
      $tone === 'pending' ? theme.colors.textMuted : theme.colors.textPrimary};
    margin: 0 0 2px;
    letter-spacing: -0.005em;
  }

  & .step-meta {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
    margin: 0;
    line-height: 1.5;
  }
`;

const SuccessActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const PrimaryAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }
`;

const SecondaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: transparent;
  color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  text-decoration: none;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }
`;

// =============================================================================
// TYPES (preserved as-is)
// =============================================================================

export enum InquiryType {
  FEATURE_REQUEST = 'feature_request',
  BUG_REPORT = 'bug_report',
  TECHNICAL_SUPPORT = 'technical_support',
  BILLING_QUESTION = 'billing_question',
  PARTNERSHIP_INQUIRY = 'partnership_inquiry',
  GENERAL_QUESTION = 'general_question',
  FEEDBACK = 'feedback',
  OTHER = 'other',
}

export enum Priority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  inquiry_type: InquiryType;
  priority: Priority;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  inquiry_type?: string;
  general?: string;
}

// Inquiry types with refined lucide icons (replacing the previous emoji set).
const INQUIRY_TYPES = [
  {
    type: InquiryType.TECHNICAL_SUPPORT,
    Icon: LifeBuoy,
    title: 'Technical Support',
    description: 'Get help with technical issues or bugs',
  },
  {
    type: InquiryType.FEATURE_REQUEST,
    Icon: Lightbulb,
    title: 'Feature Request',
    description: 'Suggest new features or improvements',
  },
  {
    type: InquiryType.BUG_REPORT,
    Icon: Bug,
    title: 'Bug Report',
    description: 'Report a problem or issue',
  },
  {
    type: InquiryType.BILLING_QUESTION,
    Icon: CreditCard,
    title: 'Billing Question',
    description: 'Questions about pricing or billing',
  },
  {
    type: InquiryType.PARTNERSHIP_INQUIRY,
    Icon: Handshake,
    title: 'Partnership',
    description: 'Business partnerships and integrations',
  },
  {
    type: InquiryType.GENERAL_QUESTION,
    Icon: HelpCircle,
    title: 'General Question',
    description: 'General questions about our service',
  },
  {
    type: InquiryType.FEEDBACK,
    Icon: MessageCircle,
    title: 'Feedback',
    description: 'Share your thoughts and suggestions',
  },
  {
    type: InquiryType.OTHER,
    Icon: MoreHorizontal,
    title: 'Other',
    description: "Anything else you'd like to discuss",
  },
];

const INITIAL_FORM_DATA: ContactFormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
  inquiry_type: InquiryType.GENERAL_QUESTION,
  priority: Priority.MEDIUM,
};

// Priority chip metadata — drives the colour-coded pill toggles.
const PRIORITY_OPTIONS: Array<{
  value: Priority;
  label: string;
  meta: string;
  tone: PriorityTone;
}> = [
  { value: Priority.LOW, label: 'Low', meta: 'General inquiry', tone: 'muted' },
  { value: Priority.MEDIUM, label: 'Medium', meta: 'Standard request', tone: 'primary' },
  { value: Priority.HIGH, label: 'High', meta: 'Important issue', tone: 'warn' },
  { value: Priority.URGENT, label: 'Urgent', meta: 'Critical problem', tone: 'danger' },
];

interface ContactFormProps {
  className?: string;
}

// Generate a client-side reference ID for the success state. Not persisted —
// just for visual polish. Format: SUP-XXXXXX (6 hex chars, uppercase).
const buildRef = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // omit visually ambiguous chars
  let out = '';
  for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return `SUP-${out}`;
};

const MESSAGE_MAX = 5000;

// =============================================================================
// COMPONENT
// =============================================================================

const ContactForm: React.FC<ContactFormProps> = ({ className }) => {
  const [formData, setFormData] = useState<ContactFormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string>('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    } else if (formData.subject.trim().length > 200) {
      newErrors.subject = 'Subject must be less than 200 characters';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.trim().length > 5000) {
      newErrors.message = 'Message must be less than 5000 characters';
    } else if (formData.message.trim().split(' ').length < 5) {
      newErrors.message = 'Please provide more detailed information (at least 5 words)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast('Please fix the errors in the form', 'error');
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        (process.env.NODE_ENV === 'production'
          ? 'https://api-beta-open-tender.nat-quest.com'
          : 'http://localhost:2024');
      const response = await fetch(`${apiBaseUrl}/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add auth header if user is logged in
          ...(localStorage.getItem('auth_token') && {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          }),
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 429) {
        const errorData = await response.json();
        setErrors({ general: errorData.detail || 'Rate limit exceeded. Please try again later.' });
        showToast('Rate limit exceeded. Please try again later.', 'error');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit contact form');
      }

      const result = await response.json();
      console.log('Contact form submitted:', result);

      setSubmittedRef(buildRef());
      setIsSubmitted(true);
      setFormData(INITIAL_FORM_DATA);
      showToast("Thank you for your message! We'll get back to you soon.", 'success');

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit contact form';
      setErrors({ general: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: keyof ContactFormData,
    value: string | InquiryType | Priority,
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field-specific error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const messageLength = formData.message.length;
  const maxMessageLength = MESSAGE_MAX;
  const isNearLimit = messageLength > maxMessageLength * 0.9;

  // Memoized so the icon component for the selected inquiry type is cheap to look up.
  const selectedInquiryIcon = useMemo(() => {
    const found = INQUIRY_TYPES.find(t => t.type === formData.inquiry_type);
    return found?.Icon ?? HelpCircle;
  }, [formData.inquiry_type]);

  // ---------------------------------------------------------------------------
  // SUCCESS STATE
  // ---------------------------------------------------------------------------
  if (isSubmitted) {
    const SelectedIcon = selectedInquiryIcon;
    return (
      <div className={className}>
        <SuccessShell>
          <SuccessIcon>
            <CheckCircle2 size={44} strokeWidth={2.25} />
          </SuccessIcon>
          <SuccessTitle>Message received.</SuccessTitle>
          <SuccessSubtitle>
            Thank you for reaching out. Our team will review your message and get back to you within
            24–48 hours.
          </SuccessSubtitle>

          <RefBadge>
            <Hash size={12} className="icon" />
            Ref · <code>{submittedRef}</code>
          </RefBadge>

          <Timeline>
            <TimelineStep $tone="done">
              <span className="marker">
                <CheckCircle2 size={16} strokeWidth={2.5} />
              </span>
              <div className="body">
                <h4 className="step-title">Message received</h4>
                <p className="step-meta">Routed to the support queue. Encrypted in transit.</p>
              </div>
            </TimelineStep>

            <TimelineStep $tone="current">
              <span className="marker">
                <Clock size={16} strokeWidth={2.5} />
              </span>
              <div className="body">
                <h4 className="step-title">Our team is reviewing</h4>
                <p className="step-meta">
                  An engineer is taking a first look — typically within a few hours of office time.
                </p>
              </div>
            </TimelineStep>

            <TimelineStep $tone="pending">
              <span className="marker">
                <Mail size={16} strokeWidth={2.5} />
              </span>
              <div className="body">
                <h4 className="step-title">You'll hear back</h4>
                <p className="step-meta">
                  A reply lands in your inbox within 24–48h (or 4h for urgent).
                </p>
              </div>
            </TimelineStep>
          </Timeline>

          <SuccessActions>
            <PrimaryAction
              type="button"
              onClick={() => {
                setIsSubmitted(false);
                setSubmittedRef('');
              }}
            >
              <SelectedIcon size={16} />
              Send another message
            </PrimaryAction>
            <SecondaryAction href="/">
              Back to dashboard
              <ArrowRight size={14} />
            </SecondaryAction>
          </SuccessActions>
        </SuccessShell>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // MAIN FORM
  // ---------------------------------------------------------------------------
  return (
    <div className={className}>
      <PageHeader>
        <HeaderLeft>
          <Crumb>Support</Crumb>
          <HeroTitle>Get in touch</HeroTitle>
          <HeroSubtitle>
            Have a question, hit a bug, or want to suggest a feature? Pick the right channel below
            and we'll route it to the team that can help.
          </HeroSubtitle>
        </HeaderLeft>
        <ResponsePill aria-label="We typically reply within 24 to 48 hours">
          <span className="pulse" />
          <span>
            <strong>Replies in 24–48h</strong>
          </span>
        </ResponsePill>
      </PageHeader>

      <Body>
        <InfoRail>
          <ReplyCard>
            <ReplyEyebrow>
              <span className="dot" />
              Reply window
            </ReplyEyebrow>
            <ReplyTitle>We answer real messages, not tickets.</ReplyTitle>
            <PromiseList>
              <PromiseItem>
                <span className="icon">
                  <Clock size={14} />
                </span>
                <div className="body">
                  <span className="strong">Standard reply</span>
                  <span className="lede">≤ 24–48h · weekdays</span>
                </div>
              </PromiseItem>
              <PromiseItem>
                <span className="icon">
                  <AlertCircle size={14} />
                </span>
                <div className="body">
                  <span className="strong">Urgent issues</span>
                  <span className="lede">≤ 4h · pick "Urgent" priority</span>
                </div>
              </PromiseItem>
              <PromiseItem>
                <span className="icon">
                  <Mail size={14} />
                </span>
                <div className="body">
                  <span className="strong">Status emails</span>
                  <span className="lede">Sent at every state change</span>
                </div>
              </PromiseItem>
            </PromiseList>
          </ReplyCard>

          <LinksCard>
            <LinksHeading>Looking for…</LinksHeading>
            <LinkRow href="/knowledge-base">
              <span className="icon">
                <Database size={14} />
              </span>
              <div className="body">
                <div className="label">Knowledge Base</div>
                <div className="desc">Self-serve guides, FAQs, and worked examples.</div>
              </div>
              <ChevronRight size={16} className="chev" />
            </LinkRow>
            <LinkRow href="/settings">
              <span className="icon">
                <SettingsIcon size={14} />
              </span>
              <div className="body">
                <div className="label">Settings</div>
                <div className="desc">Account, notifications, privacy &amp; workflow.</div>
              </div>
              <ChevronRight size={16} className="chev" />
            </LinkRow>
            <LinkRow href="/profile">
              <span className="icon">
                <User size={14} />
              </span>
              <div className="body">
                <div className="label">Profile</div>
                <div className="desc">Identity, photo, and company description.</div>
              </div>
              <ChevronRight size={16} className="chev" />
            </LinkRow>
          </LinksCard>
        </InfoRail>

        <FormCard>
          <form onSubmit={handleSubmit} noValidate>
            <FormBody>
              {errors.general && (
                <ErrorBand role="alert">
                  <span className="icon">
                    <AlertCircle size={14} />
                  </span>
                  <p className="copy">{errors.general}</p>
                </ErrorBand>
              )}

              {/* ----- 01 · YOU ----- */}
              <SubSection $first>
                <Eyebrow>
                  <span className="num">01</span>
                  <span>·</span>
                  <span>You</span>
                </Eyebrow>
                <SubHeading>Who's writing?</SubHeading>
                <SubHelper>So we can address you by name and reply to the right inbox.</SubHelper>

                <FieldGrid>
                  <FormField>
                    <FieldLabel htmlFor="name">
                      <span className="icon">
                        <User size={12} />
                      </span>
                      Full name<span className="req">*</span>
                    </FieldLabel>
                    <TextInput
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={e => handleInputChange('name', e.target.value)}
                      placeholder="Your full name"
                      $hasError={!!errors.name}
                      disabled={isSubmitting}
                      maxLength={100}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {errors.name && (
                      <FieldError id="name-error">
                        <AlertCircle size={12} />
                        {errors.name}
                      </FieldError>
                    )}
                  </FormField>

                  <FormField>
                    <FieldLabel htmlFor="email">
                      <span className="icon">
                        <Mail size={12} />
                      </span>
                      Email address<span className="req">*</span>
                    </FieldLabel>
                    <TextInput
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      placeholder="you@company.com"
                      $hasError={!!errors.email}
                      disabled={isSubmitting}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {errors.email && (
                      <FieldError id="email-error">
                        <AlertCircle size={12} />
                        {errors.email}
                      </FieldError>
                    )}
                  </FormField>
                </FieldGrid>
              </SubSection>

              {/* ----- 02 · WHAT ----- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">02</span>
                  <span>·</span>
                  <span>What</span>
                </Eyebrow>
                <SubHeading>Pick the closest channel</SubHeading>
                <SubHelper>
                  This routes your message to the right team. Don't overthink it — &quot;Other&quot; is fine.
                </SubHelper>

                <TileGrid role="radiogroup" aria-label="Inquiry type">
                  {INQUIRY_TYPES.map(({ type, Icon, title, description }) => {
                    const selected = formData.inquiry_type === type;
                    return (
                      <Tile
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        $selected={selected}
                        onClick={() => handleInputChange('inquiry_type', type)}
                        disabled={isSubmitting}
                      >
                        <TileIcon $selected={selected}>
                          <Icon size={18} />
                        </TileIcon>
                        <TileTitle $selected={selected}>{title}</TileTitle>
                        <TileDesc>{description}</TileDesc>
                        {selected && (
                          <TileCheck aria-hidden="true">
                            <CheckCircle2 size={12} strokeWidth={2.5} />
                          </TileCheck>
                        )}
                      </Tile>
                    );
                  })}
                </TileGrid>
              </SubSection>

              {/* ----- 03 · DETAIL ----- */}
              <SubSection>
                <Eyebrow>
                  <span className="num">03</span>
                  <span>·</span>
                  <span>Detail</span>
                </Eyebrow>
                <SubHeading>Tell us what's going on</SubHeading>
                <SubHelper>
                  A short subject line, the full story, and a priority that matches the impact.
                </SubHelper>

                <FormField $fullWidth style={{ marginBottom: theme.spacing[5] }}>
                  <FieldLabel htmlFor="subject">
                    <span className="icon">
                      <MessageSquare size={12} />
                    </span>
                    Subject<span className="req">*</span>
                  </FieldLabel>
                  <TextInput
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={e => handleInputChange('subject', e.target.value)}
                    placeholder="One line that summarises your message"
                    $hasError={!!errors.subject}
                    disabled={isSubmitting}
                    maxLength={200}
                    aria-invalid={!!errors.subject}
                    aria-describedby={errors.subject ? 'subject-error' : undefined}
                  />
                  {errors.subject && (
                    <FieldError id="subject-error">
                      <AlertCircle size={12} />
                      {errors.subject}
                    </FieldError>
                  )}
                </FormField>

                <FormField $fullWidth style={{ marginBottom: theme.spacing[5] }}>
                  <FieldLabel as="div">
                    <span className="icon">
                      <AlertCircle size={12} />
                    </span>
                    Priority
                  </FieldLabel>
                  <ChipRow role="radiogroup" aria-label="Priority level">
                    {PRIORITY_OPTIONS.map(opt => {
                      const selected = formData.priority === opt.value;
                      return (
                        <PriorityChip
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          $selected={selected}
                          $tone={opt.tone}
                          onClick={() => handleInputChange('priority', opt.value)}
                          disabled={isSubmitting}
                        >
                          <span className="dot" />
                          {opt.label}
                          <span style={{ opacity: 0.7, fontSize: 11, marginLeft: 4 }}>
                            · {opt.meta}
                          </span>
                        </PriorityChip>
                      );
                    })}
                  </ChipRow>
                </FormField>

                <FormField $fullWidth>
                  <FieldLabel htmlFor="message">
                    <span className="icon">
                      <MessageCircle size={12} />
                    </span>
                    Message<span className="req">*</span>
                  </FieldLabel>
                  <TextAreaField
                    id="message"
                    value={formData.message}
                    onChange={e => handleInputChange('message', e.target.value)}
                    placeholder="The more context, the better. Steps to reproduce, what you expected, what happened, links if relevant…"
                    $hasError={!!errors.message}
                    disabled={isSubmitting}
                    maxLength={maxMessageLength}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                  />
                  <CharCounter value={formData.message} max={maxMessageLength} />
                  {errors.message && (
                    <FieldError id="message-error">
                      <AlertCircle size={12} />
                      {errors.message}
                    </FieldError>
                  )}
                </FormField>
              </SubSection>
            </FormBody>

            {/* Sticky action bar — sits at the bottom of the card and follows the
                viewport while the user scrolls long messages. */}
            <ActionBar>
              <StickyMeta>
                <Lock size={12} className="icon" />
                Encrypted in transit · We typically reply in 24–48h
                {isNearLimit && (
                  <span style={{ marginLeft: theme.spacing[2], color: theme.colors.warning[700] }}>
                    · Approaching message length limit
                  </span>
                )}
              </StickyMeta>

              <SendButton type="submit" $loading={isSubmitting} disabled={isSubmitting}>
                {isSubmitting ? <ButtonSpinner /> : <Send size={14} />}
                {isSubmitting ? 'Sending…' : 'Send message'}
              </SendButton>
            </ActionBar>
          </form>
        </FormCard>
      </Body>
    </div>
  );
};

export default ContactForm;
